import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import api from '../../config/axiosConfig';

const CATEGORIES = [
  'Inflamable',
  'Corrosivo',
  'Tóxico',
  'Oxidante',
  'Criogénico',
  'Radiactivo',
  'Cancerígeno',
  'Explosivo',
  'Otro'
];

const AddHazardousSubstanceModal = ({ open, onClose, onSubstanceAdded }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'Inflamable'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async () => {
    // Validación
    if (!formData.name.trim()) {
      setError('El nombre de la sustancia es obligatorio');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/hazardous-substances-catalog', formData);
      
      // Notificar al componente padre
      if (onSubstanceAdded) {
        onSubstanceAdded(response.data);
      }

      // Limpiar formulario y cerrar
      setFormData({ name: '', description: '', category: 'Inflamable' });
      onClose();
    } catch (err) {
      console.error('Error al crear sustancia:', err);
      setError(err.response?.data || 'Error al crear la sustancia peligrosa');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', description: '', category: 'Inflamable' });
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        fontWeight: 'bold'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon />
          Agregar Nueva Sustancia Peligrosa
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Nombre de la Sustancia"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            fullWidth
            required
            placeholder="Ej: Acetona, Cloro, etc."
            autoFocus
          />

          <FormControl fullWidth>
            <InputLabel>Categoría</InputLabel>
            <Select
              value={formData.category}
              label="Categoría"
              onChange={(e) => handleChange('category', e.target.value)}
            >
              {CATEGORIES.map(cat => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Descripción (Opcional)"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Detalles adicionales sobre la sustancia..."
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={handleClose} 
          variant="outlined"
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
            }
          }}
        >
          {loading ? 'Guardando...' : 'Agregar Sustancia'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddHazardousSubstanceModal;
